import type { ElementType, FC } from 'react';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TypographyProps } from '@mui/material/Typography';
import type { TooltipProps } from '@mui/material/Tooltip';

interface TruncatedTooltipTextProps {
  text: string;
  maxWidth?: number;
  tooltipPlacement?: TooltipProps['placement'];
  noTooltip?: boolean;
  variant?: TypographyProps['variant'];
  color?: TypographyProps['color'];
  component?: ElementType;
  sx?: SxProps<Theme>;
}

export const TruncatedTooltipText: FC<TruncatedTooltipTextProps> = ({
  text,
  maxWidth = 220,
  tooltipPlacement = 'top-start',
  noTooltip,
  variant,
  color,
  component,
  sx,
}) => {
  const ellipsisStyles = useMemo(
    () => ({
      maxWidth,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }),
    [maxWidth]
  );

  const textRef = useRef<HTMLParagraphElement>(null);

  const [showTextTooltip, setShowTextTooltip] = useState(false);

  useLayoutEffect(() => {
    if (
      textRef.current &&
      textRef.current.offsetWidth < textRef.current.scrollWidth
    ) {
      setShowTextTooltip(true);
    }
  }, []);

  const typographyProps: TypographyProps = {
    variant,
    color,
    component,
    sx: [
      {
        lineHeight: 1,
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ellipsisStyles,
    ],
  };

  const typography = (
    <Typography ref={textRef} {...typographyProps}>
      {text}
    </Typography>
  );

  if (noTooltip) {
    return typography;
  }

  return (
    <Tooltip
      arrow
      placement={tooltipPlacement}
      title={showTextTooltip ? text : undefined}
    >
      {typography}
    </Tooltip>
  );
};
